import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api } from "../api/client.ts";
import type { Brand } from "../api/types.ts";
import { confirmAction, toast } from "../components/feedback.tsx";
import { Card, Empty, TableSkeleton } from "../components/ui.tsx";

interface Connection {
  id: string;
  name: string;
  baseUrl: string;
  apiKeyHint: string | null;
  hasSecret: boolean;
  isActive: boolean;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  lastSyncCount: number | null;
  lastFullSyncAt: string | null;
  brandId: string | null;
  suppressionsPushedAt: string | null;
  lastPushError: string | null;
  lastReplyPushError: string | null;
  lastPullError: string | null;
}

/** What a sync wrote; `archived` and `removed` are counted within `synced`'s pass. */
interface SyncResult {
  mode: "full" | "incremental";
  synced: number;
  archived: number;
  removed: number;
}

/**
 * The Pulse connection.
 *
 * Send mirrors Pulse's leads so segments can filter on pipeline stage, and the
 * worker keeps that mirror a few minutes behind Pulse at most. The one write
 * back is opt-outs, so the CRM's own email stops too.
 */
export function PulseSettingsPage() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const connections = useQuery({
    queryKey: ["pulse-connections"],
    queryFn: () => api.get<Connection[]>("/pulse/connections"),
  });

  const refresh = () => {
    setError(null);
    void qc.invalidateQueries({ queryKey: ["pulse-connections"] });
    void qc.invalidateQueries({ queryKey: ["reactivation"] });
  };

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<Brand[]>("/config/brands"),
  });

  const setBrand = useMutation({
    mutationFn: ({ id, brandId }: { id: string; brandId: string | null }) =>
      api.patch(`/pulse/connections/${id}`, { brandId }),
    onSuccess: () => {
      refresh();
      toast("Brand saved");
    },
    onError: (err: Error) => setError(err.message),
  });
  /**
   * "Sync now" asks for what changed; a full sync re-reads every lead, live and
   * archived, and drops the ones Pulse no longer has. The worker runs one daily,
   * but a lead archived long ago only arrives on one, so it is here too.
   */
  const sync = useMutation({
    mutationFn: ({ id, full }: { id: string; full: boolean }) =>
      api.post<SyncResult>(`/pulse/connections/${id}/sync${full ? "?full=1" : ""}`),
    onSuccess: refresh,
    onError: (err: Error) => setError(err.message),
  });
  const syncing = !sync.isPending ? null : sync.variables?.full ? "full" : "changes";
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/pulse/connections/${id}`),
    onSuccess: () => {
      refresh();
      toast("Disconnected");
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="page">
      <div className="page-header">
        <h1>Pulse</h1>
        <p>
          Leads sync every few minutes and become contacts, so segments on Pulse stage stay current
          and mail stops once a lead moves on. Pulse&apos;s opt-outs come here; Send&apos;s opt-outs
          and replies go back. Pick the brand the leads belong to: their consent is recorded for it.
        </p>
      </div>

      {error && <div className="error">{error}</div>}
      {sync.data && (
        <div className="notice">
          {sync.data.mode === "full" ? "Full sync: " : "Synced "}
          {sync.data.synced.toLocaleString()} leads
          {sync.data.archived > 0 && `, ${sync.data.archived.toLocaleString()} of them archived`}
          {sync.data.removed > 0 &&
            `; ${sync.data.removed.toLocaleString()} no longer in Pulse were dropped`}
          .
        </div>
      )}

      <Card title="Connections">
        {connections.isLoading ? (
          <TableSkeleton />
        ) : connections.data?.length === 0 ? (
          <Empty>Not connected to Pulse yet. Connect it below with an API key and secret.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Last sync</th>
                <th>Brand</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {connections.data?.map((conn) => (
                <tr key={conn.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{conn.name}</div>
                    <div className="mono" style={{ color: "var(--gray-500)", fontSize: 11 }}>
                      {conn.baseUrl}
                    </div>
                  </td>
                  <td className="mono">
                    ····{conn.apiKeyHint}
                    {!conn.hasSecret && (
                      <div style={{ color: "var(--red)", fontSize: 11, fontFamily: "inherit" }}>
                        Session token, not a key and secret. It expires when unused and is cut off
                        if the key's permissions change: disconnect and reconnect.
                      </div>
                    )}
                  </td>
                  <td>
                    <LastSync conn={conn} />
                  </td>
                  <td>
                    <select
                      aria-label="Brand these Pulse leads belong to"
                      value={conn.brandId ?? ""}
                      disabled={setBrand.isPending}
                      onChange={(e) =>
                        setBrand.mutate({ id: conn.id, brandId: e.target.value || null })
                      }
                    >
                      <option value="">None: bounces and complaints only</option>
                      {brands.data?.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                    {conn.lastPushError && (
                      <div style={{ color: "var(--red)", fontSize: 11 }}>{conn.lastPushError}</div>
                    )}
                    {conn.lastPullError && (
                      <div style={{ color: "var(--red)", fontSize: 11 }}>{conn.lastPullError}</div>
                    )}
                    {conn.lastReplyPushError && (
                      <div style={{ color: "var(--red)", fontSize: 11 }}>
                        {conn.lastReplyPushError}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <ConnectionActions
                      conn={conn}
                      syncing={syncing}
                      onSync={(full) => sync.mutate({ id: conn.id, full })}
                      removing={remove.isPending}
                      onRemove={() => remove.mutate(conn.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Connect onDone={refresh} />
    </div>
  );
}

/** When the mirror last caught up, and when it last re-read everything. */
function LastSync({ conn }: { conn: Connection }) {
  const hint = { color: "var(--gray-500)", fontSize: 11 };
  return (
    <>
      {conn.lastSyncError ? (
        <span className="pill warn">failed</span>
      ) : conn.lastSyncedAt ? (
        <>
          {new Date(conn.lastSyncedAt).toLocaleString()}
          {conn.lastSyncCount !== null && (
            <div style={hint}>{conn.lastSyncCount.toLocaleString()} leads</div>
          )}
        </>
      ) : (
        <span style={{ color: "var(--gray-500)" }}>never</span>
      )}
      {conn.lastFullSyncAt && (
        <div style={hint}>Full sync {new Date(conn.lastFullSyncAt).toLocaleString()}</div>
      )}
      {conn.lastSyncError && (
        <div style={{ color: "var(--red)", fontSize: 11 }}>{conn.lastSyncError}</div>
      )}
    </>
  );
}

/** A connection's buttons. Both syncs share one request, so either disables both. */
function ConnectionActions({
  conn,
  syncing,
  onSync,
  removing,
  onRemove,
}: {
  conn: Connection;
  syncing: "changes" | "full" | null;
  onSync: (full: boolean) => void;
  removing: boolean;
  onRemove: () => void;
}) {
  return (
    <>
      <button
        type="button"
        className="btn ghost small"
        disabled={syncing !== null}
        onClick={() => onSync(false)}
      >
        {syncing === "changes" ? "Syncing…" : "Sync now"}
      </button>{" "}
      <button
        type="button"
        className="btn ghost small"
        disabled={syncing !== null}
        onClick={async () => {
          if (
            await confirmAction({
              title: `Full sync ${conn.name}?`,
              message:
                "Re-reads every lead in Pulse, archived ones included, which can take a few minutes. Segments on Pulse stage will include archived leads from their next send.",
              confirmLabel: "Run full sync",
            })
          )
            onSync(true);
        }}
      >
        {syncing === "full" ? "Full sync…" : "Full sync"}
      </button>{" "}
      <button
        type="button"
        className="btn ghost small"
        disabled={removing}
        onClick={async () => {
          if (
            await confirmAction({
              title: `Disconnect ${conn.name}?`,
              message: "The mirrored leads go with it. Contacts made from them stay.",
              confirmLabel: "Disconnect",
              destructive: true,
            })
          )
            onRemove();
        }}
      >
        Disconnect
      </button>
    </>
  );
}

function Connect({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("Pulse");
  const [baseUrl, setBaseUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.post("/pulse/connections", { name, baseUrl, apiKey, apiSecret }),
    onSuccess: () => {
      toast("Pulse connected");
      setApiKey("");
      setApiSecret("");
      setError(null);
      onDone();
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Card title="Connect Pulse">
      <form
        className="card-body"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        {error && <div className="error">{error}</div>}

        <div className="row">
          <div className="field">
            <label htmlFor="p-name">Name</label>
            <input id="p-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
        </div>

        <div className="field">
          <label htmlFor="p-url">Base URL</label>
          <input
            id="p-url"
            className="mono"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://api.pulsecrm.com"
            required
          />
        </div>

        <div className="field">
          <label htmlFor="p-key">API key</label>
          <input
            id="p-key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            required
          />
          <div className="hint">
            Needs <span className="mono">readCRM</span> (leads),{" "}
            <span className="mono">writeCRM</span> (replies as notes),{" "}
            <span className="mono">readEmailMessages</span> and{" "}
            <span className="mono">writeEmailMessages</span> (opt-outs both ways). Checked against
            Pulse before it is stored, and sealed once it is.
          </div>
        </div>

        <div className="field">
          <label htmlFor="p-secret">API secret</label>
          <input
            id="p-secret"
            type="password"
            value={apiSecret}
            onChange={(e) => setApiSecret(e.target.value)}
            required
          />
          <div className="hint">
            Shown once, when the key is created or rotated in Pulse. Sent with the key on every
            request, so permission changes in Pulse apply straight away.
          </div>
        </div>

        <button className="btn" type="submit" disabled={create.isPending}>
          {create.isPending ? "Checking…" : "Connect"}
        </button>
      </form>
    </Card>
  );
}
